import React from 'react';
import { Badge, Avatar } from '../../../components/ui';
import { numericId } from '../../../lib/avatars';
import { Icon } from '../../../components/icons';
import { getThemeColors } from '../../../lib/theme';
import { formatDate } from '../../../lib/dateFormat';
import type { Caregiver } from '../../../types';
import {
  STATUS_TONE, AVATAR_TONE, STATUS_LABEL_KEY,
  CONTRACT_LABEL_KEY, ARBEITSSTATUS_LABEL_KEY, ARBEITSSTATUS_TONE,
} from './constants';

export function renderCaregiverCell(
  c: Caregiver,
  colId: string,
  dark: boolean,
  t: ReturnType<typeof getThemeColors>,
  tr: (key: string) => string,
): React.ReactNode {
  switch (colId) {
    case 'id':   return <span style={{ color: t.muted, fontWeight: 600, fontSize: 12 }}>{c.id}</span>;
    case 'name':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={c.name} userId={numericId(c.id)} size={32} tone={AVATAR_TONE[c.status]} dark={dark}/>
          <div>
            <div style={{ fontWeight: 600, color: t.heading }}>{c.name}</div>
            {c.email && <div style={{ fontSize: 11, color: t.muted, marginTop: 1 }}>{c.email}</div>}
          </div>
        </div>
      );
    case 'role':     return <span style={{ color: t.text }}>{c.role || '—'}</span>;
    case 'location': return <span style={{ color: t.text }}>{c.location || '—'}</span>;
    case 'phone':    return <span style={{ color: t.text }}>{c.phone || '—'}</span>;
    case 'email':    return <span style={{ color: t.text }}>{c.email || '—'}</span>;
    case 'status':   return <Badge tone={STATUS_TONE[c.status] as any} dark={dark}>{STATUS_LABEL_KEY[c.status] ? tr(STATUS_LABEL_KEY[c.status]) : c.status}</Badge>;
    case 'since':    return <span style={{ color: t.muted, fontSize: 12 }}>{c.since || '—'}</span>;
    // Spalten der alten Admin-BK-Liste (UserList.js):
    case 'vertrag':
      return c.contract
        ? <Badge tone="info" dark={dark}>{CONTRACT_LABEL_KEY[c.contract] ? tr(CONTRACT_LABEL_KEY[c.contract]) : c.contract}</Badge>
        : <span style={{ color: t.muted }}>—</span>;
    case 'arbeitsstatus':
      return (c.arbeitsstatus != null && ARBEITSSTATUS_LABEL_KEY[c.arbeitsstatus])
        ? <Badge tone={ARBEITSSTATUS_TONE[c.arbeitsstatus] as any} dark={dark}>{tr(ARBEITSSTATUS_LABEL_KEY[c.arbeitsstatus])}</Badge>
        : <span style={{ color: t.muted }}>—</span>;
    case 'invisible':
      return <span style={{ color: c.invisible ? t.muted : t.text, fontSize: 12 }}>
        {tr(c.invisible ? 'betreuung.list.visibility.hidden' : 'betreuung.list.visibility.visible')}
      </span>;
    case 'pflegegrad': return <span style={{ color: t.text }}>{c.pflegegrad ?? '—'}</span>;
    case 'land':       return <span style={{ color: t.text }}>{c.land || '—'}</span>;
    case 'verfuegbar': return <span style={{ color: t.text, fontSize: 12 }}>{formatDate(c.startdatum)}</span>;
    case 'dispo':
      // Erster Tag nach dem Ende der letzten Abwesenheit; offen = unbekannt; keine = Strich.
      if (!c.availableFrom) return <span style={{ color: t.muted }}>—</span>;
      return c.availableFrom.kind === 'date'
        ? <span data-testid="bk-available-from" title={tr('betreuung.availability.available_from_hint')} style={{ color: t.text, fontSize: 12 }}>{formatDate(c.availableFrom.date)}</span>
        : <span data-testid="bk-available-from" title={tr('betreuung.availability.available_from_hint')} style={{ color: t.muted, fontSize: 12 }}>{tr('betreuung.availability.available_from_unknown')}</span>;
    case 'origin':     return <span style={{ color: t.text }}>{c.origin || '—'}</span>;
    case 'assigned':   return <span style={{ color: t.text }}>{c.assigned_to_name || '—'}</span>;
    default:         return '—';
  }
}
